/**
 * `WorkspaceShell` — route-level wrapper around the existing
 * `<AppShell />` per WP-E1 dispatch §4 step 2.
 *
 * The architecture (`architecture.md` §3.2) reserves `<WorkspaceShell />`
 * as the parent route element for `/workspace` and its children. The
 * existing `AppShell` renders the entire workspace chrome AND the
 * canvas (header, rails, drawers, copilot dock, bottom bar, viewer
 * host). `WorkspaceShell` is therefore a thin adapter:
 *
 *   - It renders `<AppShell />` (unmodified).
 *   - It mounts an `<Outlet />` so child routes such as
 *     `/workspace/catalog`, `/workspace/settings/*`, `/workspace/help/*`
 *     can overlay the canvas with their own surface (typically a
 *     `<RouteScaffold />` until the corresponding WP-E* lands).
 *
 * IMPORTANT: AppShell itself is NOT modified by this WP. The Outlet
 * sits as a sibling so the previously-monolithic chrome continues to
 * render exactly as it did before WP-E1, satisfying AC1 of the
 * dispatch (intake regression must not surface).
 *
 * Cite:
 *   - dispatch `WP-E1-routes-foundation-1.md` §2 (canonical paths) and
 *     §4 step 2 (component map).
 *   - `architecture.md` §3.2 lines 498–535 (parent + children).
 */

import { Outlet } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';

export function WorkspaceShell() {
  return (
    <>
      <AppShell />
      <Outlet />
    </>
  );
}

/**
 * `ViewerCanvasOutlet` — the route element used for the
 * `<ViewerCanvas />` slots in `architecture.md` §3.2 lines 505 / 511 /
 * 512 (`/workspace` index, `/workspace/profile`, `/workspace/pointcloud`).
 *
 * The canvas is already rendered by the parent `<WorkspaceShell />` (via
 * `<AppShell />`); the `#mode=` deep-link fragment drives the active
 * pane per `architecture.md` §3.3 + `navigation-map.md` §5. This child
 * therefore intentionally renders nothing — using `<WorkspaceShell />`
 * here would mount a second `<AppShell />` inside the parent's `<Outlet />`,
 * which is the deviation called out in the WP-E1 result note (AC10
 * "rationale for deviation from §3.2 shape").
 */
export function ViewerCanvasOutlet() {
  return null;
}
